import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight, ArrowUpRight } from 'lucide-react';

const SLIDES = [
  {
    image: '/images/grail-exhaust-1.webp',
    title: 'GRAIL Abgasanlagen',
    desc: 'Premium Abgasanlagen von GRAIL – unvergleichlicher Sound, perfekte Verarbeitung. Für Audi, BMW, Mercedes und mehr.',
    link: '/shop?q=grail',
    price: 'ab 2.729 EUR',
    objectPos: 'center 35%',
  },
  {
    image: '/images/grail-exhaust-2.webp',
    title: 'GRAIL Klappenabgasanlagen',
    desc: 'Klappenabgasanlagen mit ECE-Zulassung. Einzigartiger Sound auf Knopfdruck – Streetlegal und TÜV-fähig.',
    link: '/shop?q=grail+klappenabgasanlage',
    price: 'ab 5.490 EUR',
    objectPos: 'center 40%',
  },
  {
    image: 'https://maxtondesign.com/cdn/shop/files/PO_GOTOWE1.jpg?v=1771494440&width=1500',
    title: 'Maxton Design Bodykits',
    desc: 'Frontansätze, Diffusoren, Seitenschweller & Spoiler. ABS & Carbon Fiber – perfekte Passform für über 400 Fahrzeugmodelle.',
    link: '/shop?q=maxton+design',
    price: 'ab 99 EUR',
  },
  {
    image: 'https://goapr-prod.nbg1.your-objectstorage.com/media/91/51/03/1699622590/ci100040_001.png',
    title: 'APR Intake Systeme',
    desc: 'Open Carbon Air Intake, PEX Ansaugung – maximaler Luftdurchsatz für spürbar mehr Leistung.',
    link: '/shop?q=apr+intake',
    price: 'ab 399 EUR',
    dark: true,
  },
];

const AUTO_SWIPE_INTERVAL = 6000; // 6 seconds

const ImageShowcase = () => {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(0);
  const timerRef = useRef(null);

  const resetTimer = () => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % SLIDES.length);
    }, AUTO_SWIPE_INTERVAL);
  };

  useEffect(() => {
    resetTimer();
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const prev = () => { setCurrent(c => (c - 1 + SLIDES.length) % SLIDES.length); resetTimer(); };
  const next = () => { setCurrent(c => (c + 1) % SLIDES.length); resetTimer(); };
  const goTo = (i) => { setCurrent(i); resetTimer(); };
  const slide = SLIDES[current];

  return (
    <section className="section" data-testid="showcase-section" style={{ overflow: 'hidden' }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '0 1.5rem' }}>
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
          style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1rem' }}>
          <div>
            <div className="caption" style={{ marginBottom: '1rem' }}>Highlights aus dem Shop</div>
            <h2 className="heading-section" style={{ fontSize: 'clamp(2rem, 5vw, 3rem)' }}>
              Top-<span style={{ color: 'var(--primary)' }}>Produkte</span>
            </h2>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={prev} data-testid="showcase-prev"
              style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid var(--border)', background: 'transparent', color: 'white', cursor: 'pointer', transition: 'border-color 0.3s' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}>
              <ChevronLeft size={20} />
            </button>
            <button onClick={next} data-testid="showcase-next"
              style={{ width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--primary)', color: 'white', cursor: 'pointer', border: 'none' }}>
              <ChevronRight size={20} />
            </button>
          </div>
        </motion.div>

        <div style={{ position: 'relative', overflow: 'hidden' }}
          onTouchStart={e => touchStart.current = e.touches[0].clientX}
          onTouchEnd={e => { const d = touchStart.current - e.changedTouches[0].clientX; if (d > 60) next(); else if (d < -60) prev(); }}>

          <motion.div key={current} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
            style={{ position: 'relative', minHeight: '560px', background: slide.dark ? '#080808' : '#0a0a0a', overflow: 'hidden', borderRadius: '12px' }}>
            {slide.dark ? (
              <div style={{
                position: 'absolute', inset: 0,
                backgroundImage: `url(${slide.image})`,
                backgroundSize: '60%', backgroundPosition: 'center right', backgroundRepeat: 'no-repeat',
              }} />
            ) : (
              <img src={slide.image} alt={slide.title}
                style={{
                  position: 'absolute', inset: 0, width: '100%', height: '100%',
                  objectFit: 'cover', objectPosition: slide.objectPos || 'center 40%',
                }}
              />
            )}
            {!slide.dark && <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.15) 55%)' }} />}
            <div style={{ position: 'relative', padding: '3rem', maxWidth: '550px', zIndex: 2, minHeight: '560px', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
              <h3 className="heading-section" style={{ fontSize: 'clamp(1.5rem, 4vw, 2.5rem)', marginBottom: '0.75rem' }}>{slide.title}</h3>
              <p style={{ color: 'var(--fg-muted)', fontSize: '1rem', lineHeight: 1.6, marginBottom: '0.75rem' }}>{slide.desc}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 800, fontSize: '1.5rem', color: 'var(--primary)' }}>{slide.price}</span>
                <a href={slide.link} target="_blank" rel="noopener noreferrer" className="btn-primary" style={{ display: 'inline-flex' }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>Im Shop ansehen <ArrowUpRight size={16} /></span>
                </a>
              </div>
            </div>
          </motion.div>

          <div style={{ position: 'absolute', bottom: '1.5rem', right: '1.5rem', display: 'flex', gap: '0.4rem', zIndex: 3 }}>
            {SLIDES.map((_, i) => (
              <button key={i} onClick={() => goTo(i)}
                style={{ width: i === current ? '28px' : '8px', height: '4px', background: i === current ? 'var(--primary)' : 'rgba(255,255,255,0.3)', border: 'none', cursor: 'pointer', transition: 'all 0.3s' }} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ImageShowcase;
